/**
 * Fleets Download Section Component
 * 
 * Section for downloading vehicles and shifts from UAT/Production environments.
 * Imports selected vehicles into a single fleet. Similar to RoutesDownloadSection but focused on vehicle and shift data.
 */

import React, { useState, useEffect, useContext, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Alert,
  AlertTitle,
  List,
  ListItem,
  ListItemText,
  Button,
  CircularProgress,
  LinearProgress,
  Stack,
  Chip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  IconButton,
  Tooltip,
} from '@mui/material';

import { fetchOperationsSolutions, fetchODBFleets, fetchNetworkSettings, type UATNetwork, type UATOperationsSolutionsResponse, type UATNetworkSettingsResponse, type UATShiftSolutionsResponse } from '../../../services/fixedRouteUatClient';
import { extractUATFleets, extractUATODBFleets, extractUATShifts } from '../../../services/fixedRouteUatImporter';
import { type Environment, getEnvironmentDescriptor, getEnvironmentDisplayName } from './EnvironmentSelector';
import { getCachedOperationsSolutions, setCachedOperationsSolutions } from './operationsSolutionsCache';
import { getCachedODBFleetsList, setCachedODBFleetsList } from './odbfleetsCache';
import { AppContext } from '../../../context/context';
import { v4 as uuid4 } from 'uuid';
import type { Fleet, Vehicle } from './FleetMenu';
import type { Point } from '../../../context/context';
import { drawerPrimaryActionSx, drawerTertiaryActionSx } from '../../../theme/componentStyles';
import { formatServiceDateLabel } from '../../../utils/dateTimeUtils';

import { Icon } from '@liftango/ui-icons';
interface FleetsDownloadSectionProps {
  jwtToken: string;
  environment: Environment;
  networks: UATNetwork[];
  isLoadingNetworks: boolean;
  networkError: string | null;
  selectedProductId: string;
  selectedNetworkId: number | null;
  onProductChange: (productId: string) => void;
  onLoadNetworks: () => void;
  serviceDate: string;
  timezone: string;
  onServiceDateChange: (date: string) => void;
  onTimezoneChange: (timezone: string) => void;
  downloadAcknowledged: boolean;
  onDownloadAcknowledgedChange: (acknowledged: boolean) => void;
}

export const FleetsDownloadSection: React.FC<FleetsDownloadSectionProps> = ({
  jwtToken,
  environment,
  networks,
  isLoadingNetworks,
  networkError,
  selectedProductId,
  selectedNetworkId,
  onProductChange,
  onLoadNetworks,
  serviceDate,
  timezone,
  onServiceDateChange,
  onTimezoneChange,
  downloadAcknowledged,
  onDownloadAcknowledgedChange,
}) => {
  // Download state (FR fleets)
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [isLoadingNetworkSettings, setIsLoadingNetworkSettings] = useState<boolean>(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [operationsSolutions, setOperationsSolutions] = useState<UATOperationsSolutionsResponse | null>(null);
  const [networkSettings, setNetworkSettings] = useState<UATNetworkSettingsResponse | null>(null);

  // Download state (ODB fleets)
  const [isDownloadingODB, setIsDownloadingODB] = useState<boolean>(false);
  const [downloadErrorODB, setDownloadErrorODB] = useState<string | null>(null);
  const [odbOperationsSolutions, setOdbOperationsSolutions] = useState<UATShiftSolutionsResponse | null>(null);

  // Preview state (FR fleets)
  const [previewFleets, setPreviewFleets] = useState<Map<string, any>>(new Map());
  const [previewShifts, setPreviewShifts] = useState<any[]>([]);
  const [expandedFleetIds, setExpandedFleetIds] = useState<Set<string>>(new Set());
  const [selectedFleetIds, setSelectedFleetIds] = useState<Set<string>>(new Set());
  const [vehicleSearchQuery, setVehicleSearchQuery] = useState<string>('');

  // Preview state (ODB fleets)
  const [odbPreviewFleets, setOdbPreviewFleets] = useState<Map<string, any>>(new Map());
  const [expandedODBFleetIds, setExpandedODBFleetIds] = useState<Set<string>>(new Set());
  const [selectedODBFleetIds, setSelectedODBFleetIds] = useState<Set<string>>(new Set());
  const [odbVehicleSearchQuery, setOdbVehicleSearchQuery] = useState<string>('');

  // Import state (FR fleets)
  const [isImporting, setIsImporting] = useState<boolean>(false);
  const [importProgress, setImportProgress] = useState<number>(0);
  const [importStep, setImportStep] = useState<string>('');
  const [importError, setImportError] = useState<string | null>(null);
  const [importResults, setImportResults] = useState<{
    fleetsCreated: number;
    vehiclesCreated: number;
    shiftsImported: number;
  } | null>(null);

  // Import state (ODB fleets)
  const [isImportingODB, setIsImportingODB] = useState<boolean>(false);
  const [importProgressODB, setImportProgressODB] = useState<number>(0);
  const [importStepODB, setImportStepODB] = useState<string>('');
  const [importErrorODB, setImportErrorODB] = useState<string | null>(null);
  const [importResultsODB, setImportResultsODB] = useState<{
    fleetsCreated: number;
    vehiclesCreated: number;
    shiftsImported: number;
  } | null>(null);

  // Get context for fleet management
  const context = useContext(AppContext);

  // Reset state when environment, JWT token, product, or network changes
  useEffect(() => {
    // Don't reset downloadAcknowledged - it should persist for the session
    setOperationsSolutions(null);
    setPreviewFleets(new Map());
    setPreviewShifts([]);
    setSelectedFleetIds(new Set());
    setDownloadError(null);
    setImportResults(null);
    setImportError(null);
    
    // Reset ODB fleet state
    setOdbOperationsSolutions(null);
    setOdbPreviewFleets(new Map());
    setSelectedODBFleetIds(new Set());
    setDownloadErrorODB(null);
    setImportResultsODB(null);
    setImportErrorODB(null);
  }, [jwtToken, environment, selectedProductId, selectedNetworkId]);

  // Auto-load network settings when product is selected
  useEffect(() => {
    if (
      downloadAcknowledged &&
      jwtToken.trim() &&
      selectedProductId &&
      !isLoadingNetworkSettings
    ) {
      handleLoadNetworkSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downloadAcknowledged, jwtToken, selectedProductId, environment]);

  // Update timezone when network settings are loaded
  useEffect(() => {
    if (networkSettings?.data?.networks && networkSettings.data.networks.length > 0) {
      const networkSettingsData = networkSettings.data.networks[0]?.networkSettings;
      if (networkSettingsData?.timezone && networkSettingsData.timezone !== timezone) {
        onTimezoneChange(networkSettingsData.timezone);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [networkSettings]);

  // Get timezone from network settings (timezone-agnostic approach)
  const effectiveTimezone = networkSettings?.data?.networks?.[0]?.networkSettings?.timezone || timezone;

  /**
   * Format date/time using the network's timezone (not user's local timezone)
   */
  const formatTimeInNetworkTimezone = (dateString: string): string => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      const options: Intl.DateTimeFormatOptions = {
        timeZone: effectiveTimezone,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
      };
      return date.toLocaleString('en-US', options);
    } catch (error) {
      console.warn('[FleetsDownloadSection] Failed to format time:', error);
      return dateString;
    }
  };

  // Auto-load data when dependencies change (both FR and ODB fleets)
  useEffect(() => {
    // Only auto-load if we have all required inputs AND user has acknowledged responsibility
    // Use effective timezone from network settings (timezone-agnostic)
    // IMPORTANT: networkSettings must be loaded for ODB fleets (needs networkId UUID)
    if (
      downloadAcknowledged &&
      jwtToken.trim() &&
      selectedProductId &&
      selectedNetworkId && // Also require network to be selected
      serviceDate &&
      effectiveTimezone.trim() &&
      networkSettings && // Ensure network settings are loaded (required for ODB fleet networkId)
      !isLoadingNetworkSettings && // Don't trigger while network settings are still loading
      !isDownloading &&
      !isDownloadingODB &&
      (!operationsSolutions || !odbOperationsSolutions)
    ) {
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (dateRegex.test(serviceDate)) {
        handleDownload(); // This now downloads both FR and ODB fleets in parallel
      }
    } else if (!downloadAcknowledged) {
      // Clear preview data when checkbox is unchecked
      setOperationsSolutions(null);
      setPreviewFleets(new Map());
      setPreviewShifts([]);
      setSelectedFleetIds(new Set());
      setDownloadError(null);
      setImportResults(null);
      setImportError(null);
      
      // Clear ODB fleet data
      setOdbOperationsSolutions(null);
      setOdbPreviewFleets(new Map());
      setSelectedODBFleetIds(new Set());
      setDownloadErrorODB(null);
      setImportResultsODB(null);
      setImportErrorODB(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downloadAcknowledged, jwtToken, selectedProductId, selectedNetworkId, serviceDate, effectiveTimezone, environment, networkSettings, isLoadingNetworkSettings]);

  const handleLoadNetworkSettings = async () => {
    if (!jwtToken.trim() || !selectedProductId) return;

    setIsLoadingNetworkSettings(true);

    try {
      const response = await fetchNetworkSettings(jwtToken, selectedProductId, environment);
      setNetworkSettings(response);
      
      // Update timezone from network settings if available
      if (response?.data?.networks && response.data.networks.length > 0) {
        const networkSettingsData = response.data.networks[0]?.networkSettings;
        if (networkSettingsData?.timezone) {
          onTimezoneChange(networkSettingsData.timezone);
        }
      }
    } catch (error) {
      console.warn('[FleetsDownloadSection] Failed to load network settings:', error);
    } finally {
      setIsLoadingNetworkSettings(false);
    }
  };

  /**
   * Download ODB fleets from UAT
   */
  const handleDownloadODB = async () => {
    if (!jwtToken.trim()) { setDownloadErrorODB('JWT token is required'); return; }
    if (!selectedProductId) { setDownloadErrorODB('Please select a product/network'); return; }
    if (!serviceDate) { setDownloadErrorODB('Service date is required'); return; }
    
    // Use timezone from network settings (timezone-agnostic)
    const effectiveTimezone = networkSettings?.data?.networks?.[0]?.networkSettings?.timezone || timezone;
    if (!effectiveTimezone.trim()) {
      setDownloadErrorODB('Timezone is required. Please wait for network settings to load.');
      return;
    }

    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(serviceDate)) { setDownloadErrorODB('Invalid date format. Please use YYYY-MM-DD'); return; }

    // Check cache first
    const cachedResponse = getCachedODBFleetsList(environment, selectedProductId, serviceDate, effectiveTimezone);
    if (cachedResponse) {
      setOdbOperationsSolutions(cachedResponse);

      // Extract ODB fleets
      const fleets = extractUATODBFleets(cachedResponse);
      setOdbPreviewFleets(fleets);

      // Select all fleets by default
      setSelectedODBFleetIds(new Set(Array.from(fleets.keys())));

      setDownloadErrorODB(null);
      return;
    }

    setIsDownloadingODB(true);
    setDownloadErrorODB(null);
    setOdbOperationsSolutions(null);
    setOdbPreviewFleets(new Map());

    try {
      const effectiveTimezone = networkSettings?.data?.networks?.[0]?.networkSettings?.timezone || timezone;
      
      // Get networkId - must be UUID string (id), not dbId number
      // Priority: Find network in networkSettings that matches selectedNetworkId (dbId), then get its UUID (id)
      let networkId: string | undefined;
      
      if (networkSettings?.data?.networks && selectedNetworkId) {
        // Find the network that matches the selectedNetworkId (dbId)
        const matchingNetwork = networkSettings.data.networks.find(
          (n: any) => n.networkSettings?.dbId === selectedNetworkId
        );
        
        if (matchingNetwork?.networkSettings?.id) {
          networkId = matchingNetwork.networkSettings.id; // UUID string
        }
      }
      
      // Fallback: use first network's UUID if no match found
      if (!networkId && networkSettings?.data?.networks?.[0]?.networkSettings?.id) {
        networkId = networkSettings.data.networks[0].networkSettings.id;
      }
      
      console.log('[FleetsDownloadSection] ODB download - networkId lookup:', {
        selectedNetworkId,
        networkSettingsCount: networkSettings?.data?.networks?.length || 0,
        allNetworkDbIds: networkSettings?.data?.networks?.map((n: any) => ({
          dbId: n.networkSettings?.dbId,
          id: n.networkSettings?.id,
        })) || [],
        matchingNetworkFound: !!networkSettings?.data?.networks?.find(
          (n: any) => n.networkSettings?.dbId === selectedNetworkId
        ),
        finalNetworkId: networkId,
        hasNetworkSettings: !!networkSettings,
      });
      
      if (!networkId) {
        setDownloadErrorODB('Network ID (UUID) is required. Please ensure network settings are loaded and a network is selected.');
        return;
      }

      console.log('[FleetsDownloadSection] Calling fetchODBFleets with:', {
        productId: selectedProductId,
        date: serviceDate,
        timezone: effectiveTimezone,
        networkId,
      });

      const response = await fetchODBFleets(
        jwtToken,
        selectedProductId,
        serviceDate,
        effectiveTimezone,
        networkId,
        environment
      );
      
      console.log('[FleetsDownloadSection] fetchODBFleets response:', {
        status: response.status,
        hasData: !!response.data,
        hasSolutions: !!response.data?.solutions,
        solutionsCount: response.data?.solutions?.length || 0,
      });

      // Cache the response
      setCachedODBFleetsList(environment, selectedProductId, serviceDate, effectiveTimezone, response);

      setOdbOperationsSolutions(response);

      // Extract ODB fleets
      console.log('[FleetsDownloadSection] Extracting ODB fleets from response:', {
        hasData: !!response.data,
        hasSolutions: !!response.data?.solutions,
        solutionsCount: response.data?.solutions?.length || 0,
      });
      const fleets = extractUATODBFleets(response);
      console.log('[FleetsDownloadSection] Extracted fleets from cache:', {
        fleetCount: fleets.size,
        fleetIds: Array.from(fleets.keys()),
      });
      setOdbPreviewFleets(fleets);

      // Select all fleets by default
      setSelectedODBFleetIds(new Set(Array.from(fleets.keys())));

      setDownloadErrorODB(null);

    } catch (error) {
      console.error('[FleetsDownloadSection] ODB download error:', error);
      let errorMessage = `Failed to download ODB fleets from ${getEnvironmentDisplayName(environment)}`;
      if (error instanceof Error) {
        errorMessage = error.message || String(error);
        if (error.message.includes('401') || error.message.includes('Authentication')) {
          errorMessage = 'Authentication failed. Please check your JWT token and try again.';
        } else if (error.message.includes('404')) {
          errorMessage = 'No ODB fleets found for the selected date and network.';
        } else if (error.message.includes('429')) {
          errorMessage = 'Too many requests. Please wait a moment and try again.';
        } else if (error.message.includes('500') || error.message.includes('502') || error.message.includes('503')) {
          errorMessage = `${getEnvironmentDisplayName(environment)} server error. Please try again in a moment.`;
        }
      } else {
        errorMessage = `Failed to download ODB fleets: ${String(error)}`;
      }
      setDownloadErrorODB(errorMessage);
    } finally {
      setIsDownloadingODB(false);
    }
  };

  const handleDownload = async () => {
    if (!jwtToken.trim()) { setDownloadError('JWT token is required'); return; }
    if (!selectedProductId) { setDownloadError('Please select a product/network'); return; }
    if (!serviceDate) { setDownloadError('Service date is required'); return; }
    
    // Use timezone from network settings (timezone-agnostic)
    const effectiveTimezone = networkSettings?.data?.networks?.[0]?.networkSettings?.timezone || timezone;
    if (!effectiveTimezone.trim()) {
      setDownloadError('Timezone is required. Please wait for network settings to load.');
      return;
    }

    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(serviceDate)) { setDownloadError('Invalid date format. Please use YYYY-MM-DD'); return; }

    // Check cache first for FR fleets
    const cachedResponse = getCachedOperationsSolutions(environment, selectedProductId, serviceDate, effectiveTimezone);
    if (cachedResponse) {
      setOperationsSolutions(cachedResponse);

      // Extract fleets and shifts
      const fleets = extractUATFleets(cachedResponse);
      setPreviewFleets(fleets);

      const shifts = extractUATShifts(cachedResponse);
      setPreviewShifts(shifts);

      // Select all fleets by default
      setSelectedFleetIds(new Set(Array.from(fleets.keys())));

      setDownloadError(null);
    } else {
    setIsDownloading(true);
    setDownloadError(null);
    setOperationsSolutions(null);
    setPreviewFleets(new Map());
    setPreviewShifts([]);
    }

    // Always try to download ODB fleets in parallel (check cache first)
    const cachedODBResponse = getCachedODBFleetsList(environment, selectedProductId, serviceDate, effectiveTimezone);
    if (cachedODBResponse) {
      setOdbOperationsSolutions(cachedODBResponse);
      const odbFleets = extractUATODBFleets(cachedODBResponse);
      setOdbPreviewFleets(odbFleets);
      setSelectedODBFleetIds(new Set(Array.from(odbFleets.keys())));
      setDownloadErrorODB(null);
    } else {
      setIsDownloadingODB(true);
      setDownloadErrorODB(null);
      setOdbOperationsSolutions(null);
      setOdbPreviewFleets(new Map());
    }

    // Fetch both FR and ODB fleets in parallel
    const fetchPromises: Promise<void>[] = [];

    // Fetch FR fleets if not cached
    if (!cachedResponse) {
      fetchPromises.push(
        (async () => {
    try {
      const effectiveTimezone = networkSettings?.data?.networks?.[0]?.networkSettings?.timezone || timezone;
      const response = await fetchOperationsSolutions(
        jwtToken,
        selectedProductId,
        serviceDate,
        effectiveTimezone,
        environment
      );

      // Cache the response
      setCachedOperationsSolutions(environment, selectedProductId, serviceDate, effectiveTimezone, response);

      setOperationsSolutions(response);

      // Extract fleets and shifts
      const fleets = extractUATFleets(response);
      setPreviewFleets(fleets);

      const shifts = extractUATShifts(response);
      setPreviewShifts(shifts);

      // Select all fleets by default
      setSelectedFleetIds(new Set(Array.from(fleets.keys())));

      setDownloadError(null);
    } catch (error) {
            let errorMessage = `Failed to download FR fleets from ${getEnvironmentDisplayName(environment)}`;
      if (error instanceof Error) {
        errorMessage = error.message;
        if (error.message.includes('401') || error.message.includes('Authentication')) {
          errorMessage = 'Authentication failed. Please check your JWT token and try again.';
        } else if (error.message.includes('404')) {
                errorMessage = 'No FR fleets found for the selected date and network.';
        } else if (error.message.includes('429')) {
          errorMessage = 'Too many requests. Please wait a moment and try again.';
        } else if (error.message.includes('500') || error.message.includes('502') || error.message.includes('503')) {
          errorMessage = `${getEnvironmentDisplayName(environment)} server error. Please try again in a moment.`;
        }
      }
      setDownloadError(errorMessage);
    } finally {
      setIsDownloading(false);
    }
        })()
      );
    }

    // Fetch ODB fleets if not cached
    if (!cachedODBResponse) {
      fetchPromises.push(
        (async () => {
          try {
            const effectiveTimezone = networkSettings?.data?.networks?.[0]?.networkSettings?.timezone || timezone;
            
            // Get networkId - must be UUID string (id), not dbId number
            // Priority: Find network in networkSettings that matches selectedNetworkId (dbId), then get its UUID (id)
            let networkId: string | undefined;
            
            if (networkSettings?.data?.networks && selectedNetworkId) {
              // Find the network that matches the selectedNetworkId (dbId)
              const matchingNetwork = networkSettings.data.networks.find(
                (n: any) => n.networkSettings?.dbId === selectedNetworkId
              );
              
              if (matchingNetwork?.networkSettings?.id) {
                networkId = matchingNetwork.networkSettings.id; // UUID string
              }
            }
            
            // Fallback: use first network's UUID if no match found
            if (!networkId && networkSettings?.data?.networks?.[0]?.networkSettings?.id) {
              networkId = networkSettings.data.networks[0].networkSettings.id;
            }
            
            console.log('[FleetsDownloadSection] ODB download (parallel) - networkId lookup:', {
              selectedNetworkId,
              networkSettingsCount: networkSettings?.data?.networks?.length || 0,
              matchingNetworkFound: !!networkSettings?.data?.networks?.find(
                (n: any) => n.networkSettings?.dbId === selectedNetworkId
              ),
              finalNetworkId: networkId,
            });
            
            if (!networkId) {
              setDownloadErrorODB('Network ID (UUID) is required. Please ensure network settings are loaded and a network is selected.');
              return;
            }

            const response = await fetchODBFleets(
              jwtToken,
              selectedProductId,
              serviceDate,
              effectiveTimezone,
              networkId,
              environment
            );

            // Cache the response
            setCachedODBFleetsList(environment, selectedProductId, serviceDate, effectiveTimezone, response);

            setOdbOperationsSolutions(response);

            // Extract ODB fleets
            console.log('[FleetsDownloadSection] Extracting ODB fleets from response:', {
              hasData: !!response.data,
              hasSolutions: !!response.data?.solutions,
              solutionsCount: response.data?.solutions?.length || 0,
            });
            const fleets = extractUATODBFleets(response);
            console.log('[FleetsDownloadSection] Extracted fleets:', {
              fleetCount: fleets.size,
              fleetIds: Array.from(fleets.keys()),
            });
            setOdbPreviewFleets(fleets);

            // Select all fleets by default
            setSelectedODBFleetIds(new Set(Array.from(fleets.keys())));

            setDownloadErrorODB(null);
          } catch (error) {
            console.error('[FleetsDownloadSection] ODB download error (parallel):', error);
            let errorMessage = `Failed to download ODB fleets from ${getEnvironmentDisplayName(environment)}`;
            if (error instanceof Error) {
              errorMessage = error.message || String(error);
              if (error.message.includes('401') || error.message.includes('Authentication')) {
                errorMessage = 'Authentication failed. Please check your JWT token and try again.';
              } else if (error.message.includes('404')) {
                errorMessage = 'No ODB fleets found for the selected date and network.';
              } else if (error.message.includes('429')) {
                errorMessage = 'Too many requests. Please wait a moment and try again.';
              } else if (error.message.includes('500') || error.message.includes('502') || error.message.includes('503')) {
                errorMessage = `${getEnvironmentDisplayName(environment)} server error. Please try again in a moment.`;
              }
            } else {
              errorMessage = `Failed to download ODB fleets: ${String(error)}`;
            }
            setDownloadErrorODB(errorMessage);
          } finally {
            setIsDownloadingODB(false);
          }
        })()
      );
    }

    // Wait for all downloads to complete
    await Promise.allSettled(fetchPromises);
  };

  const selectedProduct = networks.find(p => p.productId === selectedProductId);

  // FR fleet data
  const fleetArray = Array.from(previewFleets.values());
  const canImport = operationsSolutions && selectedFleetIds.size > 0 && !isImporting;

  // Calculate totals for selected FR vehicles
  const totalSelectedVehicles = selectedFleetIds.size;
  const selectedFleetArray = fleetArray.filter(fleet => selectedFleetIds.has(fleet.id));
  const totalShiftsInSelectedVehicles = selectedFleetArray.reduce((sum, fleet) => sum + (fleet.shifts?.length || 0), 0);

  // Filter FR vehicles based on search query
  const filteredVehicles = fleetArray.filter((fleet: any) => {
    if (!vehicleSearchQuery) return true;
    const searchLower = vehicleSearchQuery.toLowerCase();
    return (
      (fleet.name || '').toLowerCase().includes(searchLower) ||
      fleet.id.toLowerCase().includes(searchLower)
    );
  });

  // ODB fleet data
  const odbFleetArray = Array.from(odbPreviewFleets.values());
  const canImportODB = odbOperationsSolutions && selectedODBFleetIds.size > 0 && !isImportingODB;

  // Calculate totals for selected ODB vehicles
  const totalSelectedODBVehicles = selectedODBFleetIds.size;
  const selectedODBFleetArray = odbFleetArray.filter(fleet => selectedODBFleetIds.has(fleet.id));
  const totalShiftsInSelectedODBVehicles = selectedODBFleetArray.reduce((sum, fleet) => sum + (fleet.shifts?.length || 0), 0);

  // Filter ODB vehicles based on search query
  const filteredODBVehicles = odbFleetArray.filter((fleet: any) => {
    if (!odbVehicleSearchQuery) return true;
    const searchLower = odbVehicleSearchQuery.toLowerCase();
    return (
      (fleet.name || '').toLowerCase().includes(searchLower) ||
      fleet.id.toLowerCase().includes(searchLower)
    );
  });

  /**
   * Convert UAT shift to OperatingPeriod format
   * 
   * @param shift - UAT shift object with startDate and endDate (ISO datetime strings)
   * @param serviceType - Service type: 'fixed' for Fixed Route fleets, 'on-demand' for On-Demand fleets
   * @param timezone - Optional timezone (e.g., 'America/Sao_Paulo') for proper time extraction
   * @returns OperatingPeriod object with serviceType set
   */
  const convertShiftToOperatingPeriod = (shift: any, serviceType: 'fixed' | 'on-demand' = 'fixed', timezone?: string): any => {
    if (!shift.startDate || !shift.endDate) {
      return null;
    }

    // Parse ISO datetime strings and extract time in the correct timezone
    // This ensures times are timezone-agnostic by converting UTC to network timezone first
    const parseTimeFromISO = (isoString: string): string => {
      try {
        const date = new Date(isoString);
        if (isNaN(date.getTime())) {
          console.warn('[convertShiftToOperatingPeriod] Invalid date:', isoString);
          return '00:00';
        }

        // Always use timezone if provided (timezone-agnostic approach)
        // Convert UTC time from API to network timezone, then extract just the time portion
        if (timezone) {
          try {
            const formatter = new Intl.DateTimeFormat('en-US', {
              timeZone: timezone,
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
            });
            const parts = formatter.formatToParts(date);
            const hours = parts.find(p => p.type === 'hour')?.value || '00';
            const minutes = parts.find(p => p.type === 'minute')?.value || '00';
            return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
          } catch (tzError) {
            console.warn('[convertShiftToOperatingPeriod] Failed to format in timezone:', timezone, tzError);
            // If timezone conversion fails, extract time directly from string (timezone-agnostic)
            // Remove timezone info and extract time components
            const cleanTimeString = isoString.replace(/[Z+-]\d{2}:?\d{2}$|[Z]$/, '');
            const timeMatch = cleanTimeString.match(/T(\d{2}):(\d{2})/);
            if (timeMatch) {
              return `${timeMatch[1]}:${timeMatch[2]}`;
            }
            // Last resort: use UTC hours/minutes (better than local timezone)
            const hours = String(date.getUTCHours()).padStart(2, '0');
            const minutes = String(date.getUTCMinutes()).padStart(2, '0');
            return `${hours}:${minutes}`;
          }
        }

        // If no timezone provided, extract time directly from string (timezone-agnostic)
        // This preserves the time as-is without timezone conversion
        const cleanTimeString = isoString.replace(/[Z+-]\d{2}:?\d{2}$|[Z]$/, '');
        const timeMatch = cleanTimeString.match(/T(\d{2}):(\d{2})/);
        if (timeMatch) {
          return `${timeMatch[1]}:${timeMatch[2]}`;
        }
        
        // Fallback: use UTC hours/minutes (better than local timezone for timezone-agnostic)
        const hours = String(date.getUTCHours()).padStart(2, '0');
        const minutes = String(date.getUTCMinutes()).padStart(2, '0');
        return `${hours}:${minutes}`;
      } catch (e) {
        console.warn('[convertShiftToOperatingPeriod] Failed to parse time:', isoString, e);
        return '00:00';
      }
    };

    const startTime = parseTimeFromISO(shift.startDate);
    const endTime = parseTimeFromISO(shift.endDate);

    return {
      start: startTime,
      end: endTime,
      breaks: [],
      serviceType, // Set serviceType for proper vehicle categorization in simulations
    };
  };

  /**
   * Get or create a point at coordinates
   * Similar to AutoFleetGeneratorModal's getOrCreatePointAtCoordinates
   */
  // Track points we're creating in this import session to avoid duplicates
  const pointsBeingCreated = useRef<Map<string, string>>(new Map()); // key: "lat,lng", value: pointId

  const getOrCreatePointAtCoordinates = (lat: number, lng: number, name: string): string => {
    if (!context?.global) {
      return '';
    }

    const mapLayers = context.global.mapLayers || [];
    const tolerance = 0.0001; // ~11 meters
    const coordKey = `${lat.toFixed(6)},${lng.toFixed(6)}`;

    // First, check if we're already creating a point at this location in this session
    if (pointsBeingCreated.current.has(coordKey)) {
      const existingPointId = pointsBeingCreated.current.get(coordKey)!;
      console.log('[FleetsDownloadSection] Reusing point being created:', existingPointId, 'for coordinates:', coordKey);
      return existingPointId;
    }

    // Second, check if a point already exists at this location in the map
    for (const layer of mapLayers) {
      for (const shape of layer.shapes) {
        if (shape.type === 'point') {
          const point = shape as Point;
          if (point.coordinates && point.coordinates.length >= 2) {
            const dist = Math.sqrt(
              Math.pow(point.coordinates[0] - lat, 2) + 
              Math.pow(point.coordinates[1] - lng, 2)
            );
            if (dist < tolerance) {
              // Also cache it for this session
              pointsBeingCreated.current.set(coordKey, point.id);
              return point.id;
            }
          }
        }
      }
    }

    // Create a new point at the location
    const timestamp = Date.now();
    const random = Math.random().toString(36).substr(2, 9);
    const pointId = `fleet-depot-${timestamp}-${random}`;
    
    // Cache this point ID immediately to prevent duplicates
    pointsBeingCreated.current.set(coordKey, pointId);
    
    const newPoint: Point = {
      id: pointId,
      type: 'point',
      name: name,
      description: `Fleet depot location from ${getEnvironmentDisplayName(environment)}`,
      colour: '#FF9800',
      icon: 'location',
      size: 24,
      isLocked: false,
      coordinates: [lat, lng],
      hidden: false,
    };

    // Add the point to the first available layer or create a new layer
    if (context.global.setLayers) {
      const targetLayer = mapLayers.find(l => l.layerId === context.global.activeLayerId) || mapLayers[0];
      if (targetLayer) {
        newPoint.layerId = targetLayer.layerId;
        const updatedLayer = {
          ...targetLayer,
          shapes: [...targetLayer.shapes, newPoint]
        };
        context.global.setLayers(prev => 
          prev.map(l => l.layerId === targetLayer.layerId ? updatedLayer : l)
        );
      } else if (mapLayers.length > 0) {
        // Use first layer if no active layer
        newPoint.layerId = mapLayers[0].layerId;
        const updatedLayer = {
          ...mapLayers[0],
          shapes: [...mapLayers[0].shapes, newPoint]
        };
        context.global.setLayers(prev => 
          prev.map(l => l.layerId === mapLayers[0].layerId ? updatedLayer : l)
        );
      }
    }

    console.log('[FleetsDownloadSection] Created new point:', pointId, 'at coordinates:', coordKey);
    return pointId;
  };

  /**
   * Extract start and end locations from a route object
   * Helper function to extract locations from route data
   */
  const extractLocationsFromRoute = (route: any): { startLocation: string; endLocation: string } => {
    let startLocation = '';
    let endLocation = '';

    if (route && route.stops && Array.isArray(route.stops) && route.stops.length > 0) {
      const firstStop = route.stops[0];
      if (firstStop && firstStop.coordinates) {
        const coords = firstStop.coordinates;
        if (coords.lat !== undefined && coords.lng !== undefined) {
          startLocation = getOrCreatePointAtCoordinates(
            coords.lat,
            coords.lng,
            firstStop.name || firstStop.label || `Depot ${firstStop.id?.substring(0, 8) || 'Start'}`
          );
        }
      }

      const lastStop = route.stops[route.stops.length - 1];
      if (lastStop && lastStop.coordinates) {
        const coords = lastStop.coordinates;
        if (coords.lat !== undefined && coords.lng !== undefined) {
          endLocation = getOrCreatePointAtCoordinates(
            coords.lat,
            coords.lng,
            lastStop.name || lastStop.label || `Depot ${lastStop.id?.substring(0, 8) || 'End'}`
          );
        }
      }
    }

    // Also check routeStops.next if available
    if ((!startLocation || !endLocation) && route && route.routeStops && route.routeStops.next && Array.isArray(route.routeStops.next) && route.routeStops.next.length > 0) {
      const firstRouteStop = route.routeStops.next[0];
      if (firstRouteStop && firstRouteStop.coordinates && !startLocation) {
        const coords = firstRouteStop.coordinates;
        if (coords.lat !== undefined && coords.lng !== undefined) {
          startLocation = getOrCreatePointAtCoordinates(
            coords.lat,
            coords.lng,
            firstRouteStop.label || firstRouteStop.name || `Depot ${firstRouteStop.id?.substring(0, 8) || 'Start'}`
          );
        }
      }

      const lastRouteStop = route.routeStops.next[route.routeStops.next.length - 1];
      if (lastRouteStop && lastRouteStop.coordinates && !endLocation) {
        const coords = lastRouteStop.coordinates;
        if (coords.lat !== undefined && coords.lng !== undefined) {
          endLocation = getOrCreatePointAtCoordinates(
            coords.lat,
            coords.lng,
            lastRouteStop.label || lastRouteStop.name || `Depot ${lastRouteStop.id?.substring(0, 8) || 'End'}`
          );
        }
      }
    }

    return { startLocation, endLocation };
  };

  /**
   * Extract start and end locations from shifts
   * Uses route stops if available, otherwise returns empty strings
   */
  const extractLocationsFromShifts = (shifts: any[]): { startLocation: string; endLocation: string } => {
    let startLocation = '';
    let endLocation = '';

    console.log('[FleetsDownloadSection] extractLocationsFromShifts called with', shifts?.length || 0, 'shifts');
    
    // Look through all shifts to find route with stops
    for (const shift of shifts || []) {
      console.log('[FleetsDownloadSection] Processing shift:', {
        id: shift.id,
        hasRoute: !!shift.route,
        routeKeys: shift.route ? Object.keys(shift.route) : [],
        hasStops: !!(shift.route?.stops),
        stopsCount: shift.route?.stops?.length || 0,
        hasRouteStops: !!(shift.route?.routeStops),
        routeStopsNextCount: shift.route?.routeStops?.next?.length || 0,
      });

      const route = shift.route;
      if (route) {
        const locations = extractLocationsFromRoute(route);
        if (locations.startLocation && !startLocation) {
          startLocation = locations.startLocation;
        }
        if (locations.endLocation && !endLocation) {
          endLocation = locations.endLocation;
        }
        if (startLocation && endLocation) {
          break;
        }
      }
    }

    console.log('[FleetsDownloadSection] Final extracted locations:', { startLocation, endLocation });
    return { startLocation, endLocation };
  };

  /**
   * Convert UAT vehicle capacities to standard/wheelchair seats
   */
  const extractVehicleCapacities = (capacities: any[]): { standardSeats: number; wheelchairSeats: number } => {
    let standardSeats = 0;
    let wheelchairSeats = 0;

    if (capacities && Array.isArray(capacities)) {
      capacities.forEach((cap: any) => {
        if (cap.type === 'standard' || cap.type === 'STANDARD') {
          standardSeats += cap.count || 0;
        } else if (cap.type === 'wheelchair' || cap.type === 'WHEELCHAIR' || cap.type === 'accessible') {
          wheelchairSeats += cap.count || 0;
        }
      });
    }

    // Default values if no capacities found
    if (standardSeats === 0 && wheelchairSeats === 0) {
      standardSeats = 4; // Default
    }

    return { standardSeats, wheelchairSeats };
  };

  /**
   * Handle import of selected vehicles into a single fleet
   */
  const handleImport = async () => {
    if (!operationsSolutions || selectedFleetIds.size === 0) {
      setImportError('No vehicles selected for import');
      return;
    }

    if (!context?.global?.setFleets) {
      setImportError('Fleet context not available');
      return;
    }

    setIsImporting(true);
    setImportError(null);
    setImportProgress(0);
    setImportStep('Initializing import...');

    try {
      setImportStep('Processing selected vehicles...');
      setImportProgress(20);

      let vehiclesCreated = 0;
      let shiftsImported = 0;

      // Get existing fleets
      const existingFleets = context.global.fleets || [];

      // Process each selected fleet and collect all vehicles
      const selectedFleetArray = fleetArray.filter(fleet => selectedFleetIds.has(fleet.id));
      const allVehicles: Vehicle[] = [];

      setImportStep(`Processing ${selectedFleetArray.length} vehicle${selectedFleetArray.length !== 1 ? 's' : ''}...`);
      setImportProgress(40);

      for (let i = 0; i < selectedFleetArray.length; i++) {
        const uatFleet = selectedFleetArray[i];
        const progress = 40 + (i / selectedFleetArray.length) * 40;

        setImportStep(`Processing vehicle: ${uatFleet.name || `Vehicle ${uatFleet.id.substring(0, 8)}`}...`);
        setImportProgress(progress);

        // Extract capacities
        const { standardSeats, wheelchairSeats } = extractVehicleCapacities(uatFleet.capacities || []);

        // Convert shifts to operating hours with serviceType: 'fixed' for FR fleets
        // Use timezone from network settings (timezone-agnostic)
        const effectiveTimezone = networkSettings?.data?.networks?.[0]?.networkSettings?.timezone || timezone;
        const operatingHours: any[] = [];
        if (uatFleet.shifts && Array.isArray(uatFleet.shifts)) {
          uatFleet.shifts.forEach((shift: any) => {
            const operatingPeriod = convertShiftToOperatingPeriod(shift, 'fixed', effectiveTimezone);
            if (operatingPeriod) {
              operatingHours.push(operatingPeriod);
              shiftsImported++;
            }
          });
        }

        // Extract start and end locations from shifts (route stops)
        // The route data might not be in the extracted shifts, so we need to look it up from operations solutions
        let { startLocation, endLocation } = { startLocation: '', endLocation: '' };
        
        console.log('[FleetsDownloadSection] Starting location extraction for vehicle', uatFleet.id);
        console.log('[FleetsDownloadSection] operationsSolutions available:', !!operationsSolutions);
        console.log('[FleetsDownloadSection] operationsSolutions?.data available:', !!operationsSolutions?.data);
        console.log('[FleetsDownloadSection] operationsSolutions?.data?.entries available:', !!operationsSolutions?.data?.entries);
        console.log('[FleetsDownloadSection] operationsSolutions?.data?.entries length:', operationsSolutions?.data?.entries?.length || 0);
        
        // Look up route data from operations solutions for this vehicle's shifts
        // The route data is stored in operations solutions, not in the extracted fleet shifts
        if (operationsSolutions?.data?.entries) {
          console.log('[FleetsDownloadSection] Looking up route data from operations solutions for vehicle', uatFleet.id);
          console.log('[FleetsDownloadSection] Operations solutions entries count:', operationsSolutions.data.entries.length);
          
          // Get all shift IDs for this vehicle
          const vehicleShiftIds = new Set((uatFleet.shifts || []).map((s: any) => s.id));
          console.log('[FleetsDownloadSection] Vehicle shift IDs to match:', Array.from(vehicleShiftIds));
          console.log('[FleetsDownloadSection] Vehicle ID to match:', uatFleet.id);
          
          // Search through operations solutions to find routes for these shifts
          let checkedShifts = 0;
          for (const entry of operationsSolutions.data.entries) {
            if (entry.shiftSolutions) {
              for (const shiftSolution of entry.shiftSolutions) {
                checkedShifts++;
                // Check if this shift belongs to the current vehicle (by shift ID or vehicle ID)
                const matchesShiftId = vehicleShiftIds.has(shiftSolution.id);
                const matchesVehicleId = shiftSolution.vehicle?.id === uatFleet.id;
                const isVehicleShift = matchesShiftId || matchesVehicleId;
                
                if (isVehicleShift) {
                  console.log('[FleetsDownloadSection] ✓ Found matching shift solution:', {
                    shiftId: shiftSolution.id,
                    vehicleId: shiftSolution.vehicle?.id,
                    matchesShiftId,
                    matchesVehicleId,
                    hasRoute: !!shiftSolution.route,
                    routeId: shiftSolution.route?.id,
                    routeLabel: shiftSolution.route?.label,
                    routeKeys: shiftSolution.route ? Object.keys(shiftSolution.route) : [],
                  });
                  
                  const route = shiftSolution.route;
                  if (route) {
                    console.log('[FleetsDownloadSection] Route found! Extracting locations from route:', route.id);
                    console.log('[FleetsDownloadSection] Route structure:', {
                      id: route.id,
                      label: route.label,
                      hasStops: !!(route.stops),
                      stopsCount: route.stops?.length || 0,
                      hasRouteStops: !!(route.routeStops),
                      routeStopsNextCount: route.routeStops?.next?.length || 0,
                    });
                    
                    const locations = extractLocationsFromRoute(route);
                    console.log('[FleetsDownloadSection] Extracted locations from route:', locations);
                    
                    if (locations.startLocation && !startLocation) {
                      startLocation = locations.startLocation;
                      console.log('[FleetsDownloadSection] ✓ Set startLocation:', startLocation);
                    }
                    if (locations.endLocation && !endLocation) {
                      endLocation = locations.endLocation;
                      console.log('[FleetsDownloadSection] ✓ Set endLocation:', endLocation);
                    }
                    
                    if (startLocation && endLocation) {
                      console.log('[FleetsDownloadSection] ✓ Found both locations, stopping search');
                      break;
                    }
                  } else {
                    console.log('[FleetsDownloadSection] ✗ Shift matched but has no route');
                  }
                }
              }
            }
            if (startLocation && endLocation) {
              break;
            }
          }
          console.log('[FleetsDownloadSection] Checked', checkedShifts, 'shift solutions total');
        } else {
          console.log('[FleetsDownloadSection] ✗ No operations solutions available for route lookup');
        }
        
        // Fallback: try extracting from shifts directly (in case route was preserved)
        if ((!startLocation || !endLocation)) {
          const shiftLocations = extractLocationsFromShifts(uatFleet.shifts || []);
          if (shiftLocations.startLocation && !startLocation) {
            startLocation = shiftLocations.startLocation;
          }
          if (shiftLocations.endLocation && !endLocation) {
            endLocation = shiftLocations.endLocation;
          }
        }
        
        console.log('[FleetsDownloadSection] Final locations for vehicle', uatFleet.id, ':', { startLocation, endLocation });

        // Create vehicle from UAT fleet data with full metadata
        const vehicle: Vehicle = {
          id: `vehicle-${uuid4()}`,
          name: uatFleet.name || `Vehicle ${uatFleet.id.substring(0, 8)}`,
          standardSeats,
          wheelchairSeats,
          startLocation: startLocation || '', // Extract from route stops if available
          endLocation: endLocation || '', // Extract from route stops if available
          operatingHours,
          // Preserve UAT metadata
          uatVehicleId: uatFleet.id,
          uatMetadata: {
            originalId: uatFleet.id,
            originalName: uatFleet.name,
            capacities: uatFleet.capacities,
            picture: uatFleet.picture,
            importedAt: Date.now(),
            importedFrom: getEnvironmentDescriptor(environment),
            productId: selectedProductId,
            serviceDate: serviceDate,
            shifts: uatFleet.shifts, // Preserve original shift data
          },
        };

        allVehicles.push(vehicle);
        vehiclesCreated++;
      }

      setImportStep('Creating fleet...');
      setImportProgress(85);

      // Generate fleet name based on network and date
      const selectedProduct = networks.find(p => p.productId === selectedProductId);
      const productName = selectedProduct?.productName || 'UAT';
      const dateStr = serviceDate ? formatServiceDateLabel(serviceDate) : '';
      const fleetName = `${productName} Fleet${dateStr ? ` - ${dateStr}` : ''}`;

      // Create a single fleet with all vehicles and metadata
      const newFleet: Fleet = {
        id: `fleet-${uuid4()}`,
        name: fleetName,
        vehicles: allVehicles,
        // Preserve UAT metadata
        uatMetadata: {
          importedAt: Date.now(),
          importedFrom: getEnvironmentDescriptor(environment),
          productId: selectedProductId,
          serviceDate: serviceDate,
          sourceVehicleIds: selectedFleetArray.map(f => f.id), // Original UAT vehicle IDs
        },
      };

      // Add fleet to context
      context.global.setFleets((prev: Fleet[]) => [...prev, newFleet]);

      setImportProgress(100);
      setImportStep('Import complete!');

      setImportResults({
        fleetsCreated: 1,
        vehiclesCreated,
        shiftsImported,
      });

      // Clear selection after successful import
      setSelectedFleetIds(new Set());

    } catch (error) {
      console.error('[FleetsDownloadSection] Import error:', error);
      setImportError(error instanceof Error ? error.message : 'Failed to import vehicles');
    } finally {
      setIsImporting(false);
    }
  };

  /**
   * Handle import of selected ODB vehicles into a single fleet
   */
  const handleImportODB = async () => {
    if (!odbOperationsSolutions || selectedODBFleetIds.size === 0) {
      setImportErrorODB('No ODB vehicles selected for import');
      return;
    }

    if (!context?.global?.setFleets) {
      setImportErrorODB('Fleet context not available');
      return;
    }

    // Clear the points tracking map for this import session
    pointsBeingCreated.current.clear();

    setIsImportingODB(true);
    setImportErrorODB(null);
    setImportProgressODB(0);
    setImportStepODB('Initializing import...');

    try {
      setImportStepODB('Processing selected ODB vehicles...');
      setImportProgressODB(20);

      let vehiclesCreated = 0;
      let shiftsImported = 0;

      // Get existing fleets
      const existingFleets = context.global.fleets || [];

      // Process each selected ODB fleet and collect all vehicles
      const odbFleetArray = Array.from(odbPreviewFleets.values());
      const selectedODBFleetArray = odbFleetArray.filter(fleet => selectedODBFleetIds.has(fleet.id));
      const allVehicles: Vehicle[] = [];

      setImportStepODB(`Processing ${selectedODBFleetArray.length} ODB vehicle${selectedODBFleetArray.length !== 1 ? 's' : ''}...`);
      setImportProgressODB(40);

      for (let i = 0; i < selectedODBFleetArray.length; i++) {
        const uatFleet = selectedODBFleetArray[i];
        const progress = 40 + (i / selectedODBFleetArray.length) * 40;

        setImportStepODB(`Processing ODB vehicle: ${uatFleet.name || `Vehicle ${uatFleet.id.substring(0, 8)}`}...`);
        setImportProgressODB(progress);

        // Extract capacities
        const { standardSeats, wheelchairSeats } = extractVehicleCapacities(uatFleet.capacities || []);

        // Convert shifts to operating hours with serviceType: 'on-demand' for ODB fleets
        // Use timezone from network settings (timezone-agnostic)
        const effectiveTimezone = networkSettings?.data?.networks?.[0]?.networkSettings?.timezone || timezone;
        const operatingHours: any[] = [];
        if (uatFleet.shifts && Array.isArray(uatFleet.shifts)) {
          uatFleet.shifts.forEach((shift: any) => {
            const operatingPeriod = convertShiftToOperatingPeriod(shift, 'on-demand', effectiveTimezone);
            if (operatingPeriod) {
              operatingHours.push(operatingPeriod);
              shiftsImported++;
            }
          });
        }

        // Extract start and end locations from ODB fleet shifts
        // ODB fleets have schedules with stops, not routes
        let { startLocation, endLocation } = { startLocation: '', endLocation: '' };
        
        console.log('[FleetsDownloadSection] ODB - Starting location extraction for vehicle', uatFleet.id);
        console.log('[FleetsDownloadSection] ODB - Shifts count:', uatFleet.shifts?.length || 0);
        
        // Look up full schedule data from odbOperationsSolutions
        if (odbOperationsSolutions?.data?.solutions) {
          console.log('[FleetsDownloadSection] ODB - Looking up schedule data from odbOperationsSolutions');
          console.log('[FleetsDownloadSection] ODB - Solutions count:', odbOperationsSolutions.data.solutions.length);
          
          // Get all schedule IDs for this vehicle
          const vehicleScheduleIds = new Set((uatFleet.shifts || []).map((s: any) => s.scheduleId || s.id));
          console.log('[FleetsDownloadSection] ODB - Vehicle schedule IDs to match:', Array.from(vehicleScheduleIds));
          
          // Search through solutions to find schedules for these shifts
          for (const solution of odbOperationsSolutions.data.solutions) {
            if (solution.schedules && Array.isArray(solution.schedules)) {
              for (const schedule of solution.schedules) {
                // Check if this schedule belongs to the current vehicle
                const matchesScheduleId = vehicleScheduleIds.has(schedule.scheduleId);
                const matchesVehicleId = schedule.vehicle?.id === uatFleet.id;
                const isVehicleSchedule = matchesScheduleId || matchesVehicleId;
                
                if (isVehicleSchedule) {
                  console.log('[FleetsDownloadSection] ODB - Found matching schedule:', {
                    scheduleId: schedule.scheduleId,
                    vehicleId: schedule.vehicle?.id,
                    hasStops: !!(schedule.stops),
                    stopsCount: schedule.stops?.length || 0,
                    hasActivities: !!(schedule.activities),
                    activitiesCount: schedule.activities?.length || 0,
                    hasPaths: !!(schedule.paths),
                  });
                  
                  // Extract locations from schedule stops
                  if (schedule.stops && Array.isArray(schedule.stops) && schedule.stops.length > 0) {
                    console.log('[FleetsDownloadSection] ODB - Extracting locations from schedule stops');
                    console.log('[FleetsDownloadSection] ODB - Full first stop object:', JSON.stringify(schedule.stops[0], null, 2));
                    
                    // Get first stop for start location
                    const firstStop = schedule.stops[0];
                    console.log('[FleetsDownloadSection] ODB - First stop:', {
                      id: firstStop?.id,
                      hasCoordinates: !!(firstStop?.coordinates),
                      coordinates: firstStop?.coordinates,
                      hasPosition: !!(firstStop?.position),
                      position: firstStop?.position,
                      hasAddress: !!(firstStop?.address),
                      addressLat: firstStop?.address?.latitude,
                      addressLng: firstStop?.address?.longitude,
                      addressLabel: firstStop?.address?.label,
                      hasLocation: !!(firstStop?.location),
                      location: firstStop?.location,
                      hasLatLng: !!(firstStop?.lat || firstStop?.lng),
                      lat: firstStop?.lat,
                      lng: firstStop?.lng,
                      keys: firstStop ? Object.keys(firstStop) : [],
                    });
                    
                    // Try different coordinate formats
                    let firstStopCoords = null;
                    if (firstStop?.coordinates) {
                      firstStopCoords = firstStop.coordinates;
                    } else if (firstStop?.position) {
                      firstStopCoords = firstStop.position;
                    } else if (firstStop?.address?.latitude !== undefined && firstStop?.address?.longitude !== undefined) {
                      // ODB stops have coordinates in address object
                      firstStopCoords = { lat: firstStop.address.latitude, lng: firstStop.address.longitude };
                    } else if (firstStop?.lat !== undefined && firstStop?.lng !== undefined) {
                      firstStopCoords = { lat: firstStop.lat, lng: firstStop.lng };
                    } else if (firstStop?.latitude !== undefined && firstStop?.longitude !== undefined) {
                      firstStopCoords = { lat: firstStop.latitude, lng: firstStop.longitude };
                    }
                    
                    // Get last stop for end location
                    const lastStop = schedule.stops[schedule.stops.length - 1];
                    console.log('[FleetsDownloadSection] ODB - Last stop:', {
                      id: lastStop?.id,
                      hasCoordinates: !!(lastStop?.coordinates),
                      coordinates: lastStop?.coordinates,
                      hasPosition: !!(lastStop?.position),
                      position: lastStop?.position,
                      hasAddress: !!(lastStop?.address),
                      addressLat: lastStop?.address?.latitude,
                      addressLng: lastStop?.address?.longitude,
                      addressLabel: lastStop?.address?.label,
                      keys: lastStop ? Object.keys(lastStop) : [],
                    });
                    
                    // Try different coordinate formats for last stop
                    let lastStopCoords = null;
                    if (lastStop?.coordinates) {
                      lastStopCoords = lastStop.coordinates;
                    } else if (lastStop?.position) {
                      lastStopCoords = lastStop.position;
                    } else if (lastStop?.address?.latitude !== undefined && lastStop?.address?.longitude !== undefined) {
                      // ODB stops have coordinates in address object
                      lastStopCoords = { lat: lastStop.address.latitude, lng: lastStop.address.longitude };
                    } else if (lastStop?.lat !== undefined && lastStop?.lng !== undefined) {
                      lastStopCoords = { lat: lastStop.lat, lng: lastStop.lng };
                    } else if (lastStop?.latitude !== undefined && lastStop?.longitude !== undefined) {
                      lastStopCoords = { lat: lastStop.latitude, lng: lastStop.longitude };
                    }
                    
                    // Create start location if we have coordinates
                    if (firstStopCoords && firstStopCoords.lat !== undefined && firstStopCoords.lng !== undefined && !startLocation) {
                      console.log('[FleetsDownloadSection] ODB - Creating start location point at:', firstStopCoords.lat, firstStopCoords.lng);
                      startLocation = getOrCreatePointAtCoordinates(
                        firstStopCoords.lat,
                        firstStopCoords.lng,
                        firstStop.name || firstStop.label || `ODB Start ${firstStop.id?.substring(0, 8) || ''}`
                      );
                      console.log('[FleetsDownloadSection] ODB - Start location point ID:', startLocation);
                      if (!startLocation) {
                        console.error('[FleetsDownloadSection] ODB - Failed to create start location point!');
                      }
                    }
                    
                    // Create end location - check if it's the same as start first
                    if (lastStopCoords && lastStopCoords.lat !== undefined && lastStopCoords.lng !== undefined && !endLocation) {
                      // Check if coordinates match start location (within tolerance)
                      const coordsMatch = firstStopCoords && startLocation &&
                        Math.abs(firstStopCoords.lat - lastStopCoords.lat) < 0.0001 &&
                        Math.abs(firstStopCoords.lng - lastStopCoords.lng) < 0.0001;
                      
                      // Also check if it's the same stop ID
                      const isSameStop = firstStop?.id === lastStop?.id;
                      
                      if ((isSameStop || coordsMatch) && startLocation) {
                        console.log('[FleetsDownloadSection] ODB - Reusing start location for end location (same stop or matching coordinates)');
                        endLocation = startLocation;
                      } else {
                        console.log('[FleetsDownloadSection] ODB - Creating end location point at:', lastStopCoords.lat, lastStopCoords.lng);
                        endLocation = getOrCreatePointAtCoordinates(
                          lastStopCoords.lat,
                          lastStopCoords.lng,
                          lastStop.name || lastStop.label || `ODB End ${lastStop.id?.substring(0, 8) || ''}`
                        );
                        console.log('[FleetsDownloadSection] ODB - End location point ID:', endLocation);
                        if (!endLocation) {
                          console.error('[FleetsDownloadSection] ODB - Failed to create end location point!');
                        }
                      }
                    }
                    
                    if (startLocation && endLocation) {
                      console.log('[FleetsDownloadSection] ODB - Found both locations, stopping search');
                      break;
                    }
                  }
                  
                  // Also check activities for location data (ODB schedules might have activities with locations)
                  if ((!startLocation || !endLocation) && schedule.activities && Array.isArray(schedule.activities) && schedule.activities.length > 0) {
                    console.log('[FleetsDownloadSection] ODB - Checking activities for locations');
                    console.log('[FleetsDownloadSection] ODB - Activities count:', schedule.activities.length);
                    console.log('[FleetsDownloadSection] ODB - Full first activity object:', JSON.stringify(schedule.activities[0], null, 2));
                    const firstActivity = schedule.activities[0];
                    const lastActivity = schedule.activities[schedule.activities.length - 1];
                    
                    // Try to extract coordinates from activities
                    if (firstActivity && !startLocation) {
                      console.log('[FleetsDownloadSection] ODB - First activity keys:', Object.keys(firstActivity));
                      let activityCoords = firstActivity.coordinates || firstActivity.position || 
                        (firstActivity.lat !== undefined ? { lat: firstActivity.lat, lng: firstActivity.lng } : null) ||
                        (firstActivity.latitude !== undefined ? { lat: firstActivity.latitude, lng: firstActivity.longitude } : null) ||
                        (firstActivity.location?.coordinates ? firstActivity.location.coordinates : null) ||
                        (firstActivity.location?.lat !== undefined ? { lat: firstActivity.location.lat, lng: firstActivity.location.lng } : null) ||
                        (firstActivity.stop?.coordinates ? firstActivity.stop.coordinates : null) ||
                        (firstActivity.stop?.position ? firstActivity.position : null);
                      
                      console.log('[FleetsDownloadSection] ODB - Extracted activity coords:', activityCoords);
                      if (activityCoords && activityCoords.lat !== undefined && activityCoords.lng !== undefined) {
                        console.log('[FleetsDownloadSection] ODB - Creating start location from activity');
                        startLocation = getOrCreatePointAtCoordinates(
                          activityCoords.lat,
                          activityCoords.lng,
                          firstActivity.name || firstActivity.label || firstActivity.stop?.name || `ODB Activity Start`
                        );
                        console.log('[FleetsDownloadSection] ODB - Start location point ID:', startLocation);
                      }
                    }
                    
                    if (lastActivity && !endLocation) {
                      console.log('[FleetsDownloadSection] ODB - Last activity keys:', Object.keys(lastActivity));
                      let activityCoords = lastActivity.coordinates || lastActivity.position || 
                        (lastActivity.lat !== undefined ? { lat: lastActivity.lat, lng: lastActivity.lng } : null) ||
                        (lastActivity.latitude !== undefined ? { lat: lastActivity.latitude, lng: lastActivity.longitude } : null) ||
                        (lastActivity.location?.coordinates ? lastActivity.location.coordinates : null) ||
                        (lastActivity.location?.lat !== undefined ? { lat: lastActivity.location.lat, lng: lastActivity.location.lng } : null) ||
                        (lastActivity.stop?.coordinates ? lastActivity.stop.coordinates : null) ||
                        (lastActivity.stop?.position ? lastActivity.position : null);
                      
                      console.log('[FleetsDownloadSection] ODB - Extracted activity coords:', activityCoords);
                      if (activityCoords && activityCoords.lat !== undefined && activityCoords.lng !== undefined) {
                        console.log('[FleetsDownloadSection] ODB - Creating end location from activity');
                        endLocation = getOrCreatePointAtCoordinates(
                          activityCoords.lat,
                          activityCoords.lng,
                          lastActivity.name || lastActivity.label || lastActivity.stop?.name || `ODB Activity End`
                        );
                        console.log('[FleetsDownloadSection] ODB - End location point ID:', endLocation);
                      }
                    }
                  }
                  
                  if (startLocation && endLocation) {
                    break;
                  }
                }
              }
            }
            if (startLocation && endLocation) {
              break;
            }
          }
        }
        
        console.log('[FleetsDownloadSection] ODB - Final locations for vehicle', uatFleet.id, ':', { startLocation, endLocation });

        // Create vehicle from UAT fleet data with full metadata
        const vehicle: Vehicle = {
          id: `vehicle-${uuid4()}`,
          name: uatFleet.name || `ODB Vehicle ${uatFleet.id.substring(0, 8)}`,
          standardSeats,
          wheelchairSeats,
          startLocation: startLocation || '', // Extract from route stops if available
          endLocation: endLocation || '', // Extract from route stops if available
          operatingHours,
          // Preserve UAT metadata
          uatVehicleId: uatFleet.id,
          uatMetadata: {
            originalId: uatFleet.id,
            originalName: uatFleet.name,
            capacities: uatFleet.capacities,
            picture: uatFleet.picture,
            importedAt: Date.now(),
            importedFrom: getEnvironmentDescriptor(environment),
            productId: selectedProductId,
            serviceDate: serviceDate,
            shifts: uatFleet.shifts, // Preserve original shift data
          },
        };

        allVehicles.push(vehicle);
        vehiclesCreated++;
      }

      setImportStepODB('Creating ODB fleet...');
      setImportProgressODB(85);

      // Generate fleet name based on network and date
      const selectedProduct = networks.find(p => p.productId === selectedProductId);
      const productName = selectedProduct?.productName || 'UAT';
      const dateStr = serviceDate ? formatServiceDateLabel(serviceDate) : '';
      const fleetName = `${productName} ODB Fleet${dateStr ? ` - ${dateStr}` : ''}`;

      // Create a single fleet with all ODB vehicles and metadata
      const newFleet: Fleet = {
        id: `fleet-${uuid4()}`,
        name: fleetName,
        vehicles: allVehicles,
        // Preserve UAT metadata
        uatMetadata: {
          importedAt: Date.now(),
          importedFrom: getEnvironmentDescriptor(environment),
          productId: selectedProductId,
          serviceDate: serviceDate,
          sourceVehicleIds: selectedODBFleetArray.map(f => f.id), // Original UAT vehicle IDs
        },
      };

      // Add fleet to context
      context.global.setFleets((prev: Fleet[]) => [...prev, newFleet]);

      setImportProgressODB(100);
      setImportStepODB('Import complete!');

      setImportResultsODB({
        fleetsCreated: 1,
        vehiclesCreated,
        shiftsImported,
      });

      // Clear selection after successful import
      setSelectedODBFleetIds(new Set());

    } catch (error) {
      console.error('[FleetsDownloadSection] ODB Import error:', error);
      setImportErrorODB(error instanceof Error ? error.message : 'Failed to import ODB vehicles');
    } finally {
      setIsImportingODB(false);
    }
  };

  // Show placeholder if consent not acknowledged
  if (!downloadAcknowledged) {
    return (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        width: '100%',
        flex: 1,
        minHeight: 0,
        p: 2,
        boxSizing: 'border-box',
        overflow: 'hidden'
      }}>
        <img 
          src="/icons/consent-img.png" 
          alt="Consent Required" 
          style={{ 
            maxWidth: '100%',
            maxHeight: '100%',
            width: 'auto',
            height: 'auto',
            objectFit: 'contain',
            objectPosition: 'center center'
          }} 
        />
      </Box>
    );
  }

  return (
    <Box>
          <Stack spacing={2}>
        {/* Error messages */}
            {networkError && (
              <Alert severity="error">
                {networkError}
              </Alert>
            )}

        {(isDownloading || isDownloadingODB) && (
              <Box>
                <LinearProgress />
                <Typography variant="caption" sx={{ mt: 0.5, display: 'block', textAlign: 'left' }}>
              Loading fleets...
                </Typography>
              </Box>
            )}

        {(downloadError || downloadErrorODB) && (
          <>
            {downloadError && (
              <Alert severity="error">
                <AlertTitle>FR Fleet Download Failed</AlertTitle>
                {downloadError}
              </Alert>
            )}
            {downloadErrorODB && (
              <Alert severity="error">
                <AlertTitle>ODB Fleet Download Failed</AlertTitle>
                {downloadErrorODB}
              </Alert>
            )}
          </>
        )}

        {/* Two-column layout for FR and ODB fleets */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          {/* Fixed Route Fleets Section */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="h6">
                Fixed Route Fleets
              </Typography>
              <Tooltip 
                title="Fixed Route fleets contain vehicles that operate on predefined routes with scheduled stops. These vehicles are used for traditional transit service and will be categorized as 'fixed' service type in simulations."
                placement="top"
                arrow
              >
                <Icon name="circle-info" size={20} style={{ color: '#52565e', cursor: 'help' }} />
              </Tooltip>
            </Box>
            <Divider sx={{ mb: 2 }} />

            {operationsSolutions ? (
              <Stack spacing={2}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="subtitle2" fontWeight={600}>
                        Preview ({previewFleets.size} vehicle{previewFleets.size !== 1 ? 's' : ''})
                    </Typography>
                      <Tooltip 
                        title="Preview all available Fixed Route vehicles from the selected network and service date. Select vehicles to import into Atlas."
                        placement="top"
                        arrow
                      >
                        <Icon name="circle-info" size={20} style={{ color: '#52565e', cursor: 'help' }} />
                      </Tooltip>
                    </Box>
                    <Button
                      size="small"
                      onClick={() => {
                        if (selectedFleetIds.size === previewFleets.size) {
                          setSelectedFleetIds(new Set());
                        } else {
                          setSelectedFleetIds(new Set(Array.from(previewFleets.keys())));
                        }
                      }}
                      disabled={isImporting}
                      sx={(theme) => drawerTertiaryActionSx(theme)}
                    >
                      {selectedFleetIds.size === previewFleets.size ? 'Deselect All' : 'Select All'}
                    </Button>
                  </Box>
                  
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search FR vehicles..."
                    value={vehicleSearchQuery}
                    onChange={(e) => setVehicleSearchQuery(e.target.value)}
                    disabled={isImporting}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Icon name="magnifying-glass" size={20} />
                        </InputAdornment>
                      ),
                      endAdornment: vehicleSearchQuery && (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => setVehicleSearchQuery('')}
                            edge="end"
                          >
                            <Icon name="xmark" size={20} />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 1 }}
                  />

                  <List dense sx={{ maxHeight: '60vh', overflow: 'auto' }}>
                    {filteredVehicles.length === 0 ? (
                      <ListItem>
                        <ListItemText
                          primary="No FR vehicles found"
                          secondary={vehicleSearchQuery ? 'Try adjusting your search query' : 'No FR vehicles available'}
                          primaryTypographyProps={{ variant: 'body2' }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItem>
                    ) : (
                      filteredVehicles.map((fleet) => {
                        const isSelected = selectedFleetIds.has(fleet.id);
                        return (
                          <ListItem key={fleet.id} sx={{ py: 0.5 }}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  size="small"
                                  checked={isSelected}
                                  onChange={(e) => {
                                    const newSet = new Set(selectedFleetIds);
                                    if (e.target.checked) {
                                      newSet.add(fleet.id);
                                    } else {
                                      newSet.delete(fleet.id);
                                    }
                                    setSelectedFleetIds(newSet);
                                  }}
                                  disabled={isImporting}
                                />
                              }
                              label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                                  <Box
                                    sx={{
                                      width: 40,
                                      height: 40,
                                      borderRadius: '50%',
                                      bgcolor: 'primary.main',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      flexShrink: 0,
                                    }}
                                  >
                                    <Icon size={20} name="bus" style={{ color: 'white'}} />
                                  </Box>
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                      {fleet.name || `Vehicle ${fleet.id.substring(0, 8)}`}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {fleet.shifts?.length || 0} shift{(fleet.shifts?.length || 0) !== 1 ? 's' : ''}
                                    </Typography>
                                  </Box>
                                </Box>
                              }
                            />
                          </ListItem>
                        );
                      })
                    )}
                  </List>
                </Box>

                {/* FR Import Button */}
                <Tooltip 
                  title={!canImport ? 'Please select at least one vehicle to import' : 'Import selected Fixed Route vehicles. Vehicles will be created with serviceType: "fixed" for simulation compatibility.'}
                  placement="top"
                  arrow
                >
                  <span>
                <Button
                  variant="contained"
                  color="primary"
                      size="medium"
                  onClick={handleImport}
                  disabled={!canImport}
                  fullWidth
                  startIcon={isImporting ? <CircularProgress size={16} /> : <Icon name="circle-check" />}
                  sx={(theme) => drawerPrimaryActionSx(theme)}
                >
                      {isImporting ? 'Importing...' : `Import ${selectedFleetIds.size} FR Vehicle${selectedFleetIds.size !== 1 ? 's' : ''}`}
                </Button>
                  </span>
                </Tooltip>

                {isImporting && (
                  <Box>
                    <LinearProgress variant="determinate" value={importProgress} />
                    <Typography variant="caption" sx={{ mt: 0.5, display: 'block', textAlign: 'left' }}>
                      {importStep || 'Importing vehicles...'} ({Math.round(importProgress)}%)
                    </Typography>
                  </Box>
                )}

                {importError && (
                  <Alert severity="error">
                    <AlertTitle>FR Import Failed</AlertTitle>
                    {importError}
                  </Alert>
                )}

                {importResults && (
                  <Alert severity="success">
                    <AlertTitle>FR Import Complete</AlertTitle>
                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                      <strong>Fleets:</strong> {importResults.fleetsCreated} created
                    </Typography>
                    <Typography variant="caption" display="block">
                      <strong>Vehicles:</strong> {importResults.vehiclesCreated} created
                    </Typography>
                    <Typography variant="caption" display="block">
                      <strong>Shifts:</strong> {importResults.shiftsImported} imported as operating hours
                    </Typography>
                  </Alert>
                )}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No FR fleets downloaded. Click "Download" to fetch FR fleets.
              </Typography>
            )}
          </Box>

          {/* On-Demand Fleets Section */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="h6">
                On-Demand Fleets
              </Typography>
              <Tooltip 
                title="On-Demand fleets contain vehicles that operate flexible, demand-responsive service. These vehicles can be dispatched to any location based on ride requests and will be categorized as 'on-demand' service type in simulations."
                placement="top"
                arrow
              >
                <Icon name="circle-info" size={20} style={{ color: '#52565e', cursor: 'help' }} />
              </Tooltip>
            </Box>
            <Divider sx={{ mb: 2 }} />

            {odbOperationsSolutions ? (
              <Stack spacing={2}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Preview ({odbPreviewFleets.size} vehicle{odbPreviewFleets.size !== 1 ? 's' : ''})
                      </Typography>
                      <Tooltip 
                        title="Preview all available On-Demand vehicles from the selected network and service date. Select vehicles to import into Atlas."
                        placement="top"
                        arrow
                      >
                        <Icon name="circle-info" size={20} style={{ color: '#52565e', cursor: 'help' }} />
                      </Tooltip>
                    </Box>
                    <Button
                      size="small"
                      onClick={() => {
                        if (selectedODBFleetIds.size === odbPreviewFleets.size) {
                          setSelectedODBFleetIds(new Set());
                        } else {
                          setSelectedODBFleetIds(new Set(Array.from(odbPreviewFleets.keys())));
                        }
                      }}
                      disabled={isImportingODB}
                      sx={(theme) => drawerTertiaryActionSx(theme)}
                    >
                      {selectedODBFleetIds.size === odbPreviewFleets.size ? 'Deselect All' : 'Select All'}
                    </Button>
                  </Box>
                  
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search ODB vehicles..."
                    value={odbVehicleSearchQuery}
                    onChange={(e) => setOdbVehicleSearchQuery(e.target.value)}
                    disabled={isImportingODB}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Icon name="magnifying-glass" size={20} />
                        </InputAdornment>
                      ),
                      endAdornment: odbVehicleSearchQuery && (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => setOdbVehicleSearchQuery('')}
                            edge="end"
                          >
                            <Icon name="xmark" size={20} />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 1 }}
                  />

                  <List dense sx={{ maxHeight: '60vh', overflow: 'auto' }}>
                    {filteredODBVehicles.length === 0 ? (
                      <ListItem>
                        <ListItemText
                          primary="No ODB vehicles found"
                          secondary={odbVehicleSearchQuery ? 'Try adjusting your search query' : 'No ODB vehicles available'}
                          primaryTypographyProps={{ variant: 'body2' }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItem>
                    ) : (
                      filteredODBVehicles.map((fleet) => {
                        const isSelected = selectedODBFleetIds.has(fleet.id);
                        return (
                          <ListItem key={fleet.id} sx={{ py: 0.5 }}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  size="small"
                                  checked={isSelected}
                                  onChange={(e) => {
                                    const newSet = new Set(selectedODBFleetIds);
                                    if (e.target.checked) {
                                      newSet.add(fleet.id);
                                    } else {
                                      newSet.delete(fleet.id);
                                    }
                                    setSelectedODBFleetIds(newSet);
                                  }}
                                  disabled={isImportingODB}
                                />
                              }
                              label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                                  <Box
                                    sx={{
                                      width: 40,
                                      height: 40,
                                      borderRadius: '50%',
                                      bgcolor: 'secondary.main',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      flexShrink: 0,
                                    }}
                                  >
                                    <Icon size={20} name="bus" style={{ color: 'white'}} />
                                  </Box>
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                      {fleet.name || `ODB Vehicle ${fleet.id.substring(0, 8)}`}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {fleet.shifts?.length || 0} shift{(fleet.shifts?.length || 0) !== 1 ? 's' : ''}
                                    </Typography>
                                  </Box>
                                </Box>
                              }
                            />
                          </ListItem>
                        );
                      })
                    )}
                  </List>
                </Box>

                {/* ODB Import Button */}
                <Tooltip 
                  title={!canImportODB ? 'Please select at least one vehicle to import' : 'Import selected On-Demand vehicles. Vehicles will be created with serviceType: "on-demand" for simulation compatibility.'}
                  placement="top"
                  arrow
                >
                  <span>
                    <Button
                      variant="contained"
                      color="secondary"
                      size="medium"
                      onClick={handleImportODB}
                      disabled={!canImportODB}
                      fullWidth
                      startIcon={isImportingODB ? <CircularProgress size={16} /> : <Icon name="circle-check" />}
                      sx={(theme) => drawerPrimaryActionSx(theme)}
                    >
                      {isImportingODB ? 'Importing...' : `Import ${selectedODBFleetIds.size} ODB Vehicle${selectedODBFleetIds.size !== 1 ? 's' : ''}`}
                    </Button>
                  </span>
                </Tooltip>

                {isImportingODB && (
                  <Box>
                    <LinearProgress variant="determinate" value={importProgressODB} />
                    <Typography variant="caption" sx={{ mt: 0.5, display: 'block', textAlign: 'left' }}>
                      {importStepODB || 'Importing ODB vehicles...'} ({Math.round(importProgressODB)}%)
                    </Typography>
                  </Box>
                )}

                {importErrorODB && (
                  <Alert severity="error">
                    <AlertTitle>ODB Import Failed</AlertTitle>
                    {importErrorODB}
                  </Alert>
                )}

                {importResultsODB && (
                  <Alert severity="success">
                    <AlertTitle>ODB Import Complete</AlertTitle>
                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                      <strong>Fleets:</strong> {importResultsODB.fleetsCreated} created
                    </Typography>
                    <Typography variant="caption" display="block">
                      <strong>Vehicles:</strong> {importResultsODB.vehiclesCreated} created
                    </Typography>
                    <Typography variant="caption" display="block">
                      <strong>Shifts:</strong> {importResultsODB.shiftsImported} imported as operating hours
                    </Typography>
                  </Alert>
            )}
          </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No ODB fleets downloaded. Click "Download" to fetch ODB fleets.
              </Typography>
            )}
        </Box>
      </Box>
      </Stack>
    </Box>
  );
};

export default FleetsDownloadSection;

